/**
 * 🗄️ SmartBoutik - Service IndexedDB pour le stockage offline
 * Version corrigée avec initialisation robuste
 */

const DB_NAME = 'SmartBoutikOffline';
const DB_VERSION = 1;

const STORES = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  CUSTOMERS: 'customers',
  PENDING_SALES: 'pendingSales',
  SALES_HISTORY: 'salesHistory',
  SYNC_QUEUE: 'syncQueue',
  META: 'meta'
};

let db = null;
let isInitializing = false;
let initPromise = null;

/**
 * ✅ Vérifier si IndexedDB est supporté
 */
const isIndexedDBSupported = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (e) {
    return false;
  }
};

/**
 * ✅ Initialiser la base de données IndexedDB
 */
export const initDatabase = () => {
  // Si déjà initialisé, retourner la DB
  if (db) {
    console.log('✅ IndexedDB déjà initialisée');
    return Promise.resolve(db);
  }

  // Si une initialisation est en cours, attendre
  if (initPromise) {
    return initPromise;
  }

  // Vérifier le support
  if (!isIndexedDBSupported()) {
    console.warn('⚠️ IndexedDB non supporté - Mode offline désactivé');
    return Promise.resolve(null);
  }

  isInitializing = true;

  initPromise = new Promise((resolve, reject) => {
    console.log('🔄 Initialisation IndexedDB...');

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('❌ Erreur ouverture IndexedDB:', event.target.error);
      isInitializing = false;
      initPromise = null;
      // Ne pas rejeter, juste résoudre avec null
      resolve(null);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      isInitializing = false;
      console.log('✅ IndexedDB initialisée avec succès');
      console.log('📋 Stores disponibles:', Array.from(db.objectStoreNames));
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('🔄 Création/Mise à jour du schéma IndexedDB...');

      // Store des produits
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productsStore = database.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
        productsStore.createIndex('barcode', 'barcode', { unique: false });
        productsStore.createIndex('sku', 'sku', { unique: false });
        productsStore.createIndex('categoryId', 'categoryId', { unique: false });
        productsStore.createIndex('name', 'name', { unique: false });
        console.log('  ✓ Store products créé');
      }

      // Store des catégories
      if (!database.objectStoreNames.contains(STORES.CATEGORIES)) {
        const categoriesStore = database.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
        categoriesStore.createIndex('name', 'name', { unique: false });
        console.log('  ✓ Store categories créé');
      }

      // Store des clients
      if (!database.objectStoreNames.contains(STORES.CUSTOMERS)) {
        const customersStore = database.createObjectStore(STORES.CUSTOMERS, { keyPath: 'id' });
        customersStore.createIndex('phone', 'phone', { unique: false });
        customersStore.createIndex('name', 'name', { unique: false });
        console.log('  ✓ Store customers créé');
      }

      // Store des ventes en attente
      if (!database.objectStoreNames.contains(STORES.PENDING_SALES)) {
        const pendingSalesStore = database.createObjectStore(STORES.PENDING_SALES, { 
          keyPath: 'localId'
        });
        pendingSalesStore.createIndex('createdAt', 'createdAt', { unique: false });
        pendingSalesStore.createIndex('status', 'status', { unique: false });
        console.log('  ✓ Store pendingSales créé');
      }

      // Store de l'historique des ventes
      if (!database.objectStoreNames.contains(STORES.SALES_HISTORY)) {
        const salesHistoryStore = database.createObjectStore(STORES.SALES_HISTORY, { keyPath: 'id' });
        salesHistoryStore.createIndex('saleDate', 'saleDate', { unique: false });
        salesHistoryStore.createIndex('customerId', 'customerId', { unique: false });
        console.log('  ✓ Store salesHistory créé');
      }

      // File d'attente de synchronisation
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncQueueStore = database.createObjectStore(STORES.SYNC_QUEUE, { 
          keyPath: 'id',
          autoIncrement: true 
        });
        syncQueueStore.createIndex('type', 'type', { unique: false });
        syncQueueStore.createIndex('priority', 'priority', { unique: false });
        syncQueueStore.createIndex('createdAt', 'createdAt', { unique: false });
        console.log('  ✓ Store syncQueue créé');
      }

      // Métadonnées
      if (!database.objectStoreNames.contains(STORES.META)) {
        database.createObjectStore(STORES.META, { keyPath: 'key' });
        console.log('  ✓ Store meta créé');
      }

      console.log('✅ Schéma IndexedDB créé avec succès');
    };

    // Timeout de sécurité
    setTimeout(() => {
      if (isInitializing) {
        console.warn('⚠️ Timeout initialisation IndexedDB');
        isInitializing = false;
        initPromise = null;
        resolve(null);
      }
    }, 5000);
  });

  return initPromise;
};

/**
 * ✅ Vérifier si la DB est prête
 */
export const isReady = () => {
  return db !== null;
};

/**
 * ✅ Obtenir une transaction sur un store (avec vérification)
 */
const getTransaction = (storeName, mode = 'readonly') => {
  if (!db) {
    throw new Error('Base de données non initialisée');
  }
  if (!db.objectStoreNames.contains(storeName)) {
    throw new Error(`Store "${storeName}" non trouvé`);
  }
  return db.transaction(storeName, mode);
};

/**
 * ✅ Obtenir un store (avec vérification)
 */
const getStore = (storeName, mode = 'readonly') => {
  return getTransaction(storeName, mode).objectStore(storeName);
};

/**
 * ✅ Helper pour exécuter une opération de manière sécurisée
 */
const safeOperation = async (operation, fallback = null) => {
  if (!db) {
    console.warn('⚠️ IndexedDB non disponible, utilisation du fallback');
    return fallback;
  }
  try {
    return await operation();
  } catch (error) {
    console.error('❌ Erreur IndexedDB:', error);
    return fallback;
  }
};

// ============================================
// 📦 OPÉRATIONS SUR LES PRODUITS
// ============================================

export const saveProducts = async (products) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PRODUCTS, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      const transaction = store.transaction;

      products.forEach(product => {
        store.put({
          ...product,
          _lastSync: new Date().toISOString()
        });
        count++;
      });

      transaction.oncomplete = () => {
        console.log(`✅ ${count} produits sauvegardés en local`);
        resolve(count);
      };

      transaction.onerror = (event) => {
        console.error('❌ Erreur sauvegarde produits:', event.target.error);
        reject(event.target.error);
      };
    });
  }, 0);
};

export const getProducts = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.PRODUCTS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        console.log(`📦 ${request.result.length} produits récupérés du cache`);
        resolve(request.result);
      };
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }, []);
};

export const getProductByBarcode = async (barcode) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PRODUCTS);
    const index = store.index('barcode');
    
    return new Promise((resolve, reject) => {
      const request = index.get(barcode);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = (event) => reject(event.target.error);
    });
  }, null);
};

export const searchProductsByName = async (searchTerm) => {
  const products = await getProducts();
  if (!products || products.length === 0) return [];
  
  const term = searchTerm.toLowerCase();
  return products.filter(p => 
    p.name?.toLowerCase().includes(term) ||
    p.sku?.toLowerCase().includes(term) ||
    p.barcode?.toLowerCase().includes(term)
  );
};

export const updateLocalStock = async (productId, newQuantity) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PRODUCTS, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(productId);
      
      getRequest.onsuccess = () => {
        const product = getRequest.result;
        if (product) {
          product.stockQuantity = newQuantity;
          product.currentStock = newQuantity;
          product._localModified = true;
          product._lastModified = new Date().toISOString();
          
          const putRequest = store.put(product);
          putRequest.onsuccess = () => resolve(product);
          putRequest.onerror = (e) => reject(e.target.error);
        } else {
          resolve(null);
        }
      };
      
      getRequest.onerror = (event) => reject(event.target.error);
    });
  }, null);
};

// ============================================
// 🏷️ OPÉRATIONS SUR LES CATÉGORIES
// ============================================

export const saveCategories = async (categories) => {
  return safeOperation(async () => {
    const store = getStore(STORES.CATEGORIES, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      const transaction = store.transaction;

      categories.forEach(category => {
        store.put({
          ...category,
          _lastSync: new Date().toISOString()
        });
        count++;
      });

      transaction.oncomplete = () => {
        console.log(`✅ ${count} catégories sauvegardées en local`);
        resolve(count);
      };

      transaction.onerror = (event) => reject(event.target.error);
    });
  }, 0);
};

export const getCategories = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.CATEGORIES);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }, []);
};

// ============================================
// 👤 OPÉRATIONS SUR LES CLIENTS
// ============================================

export const saveCustomers = async (customers) => {
  return safeOperation(async () => {
    const store = getStore(STORES.CUSTOMERS, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      const transaction = store.transaction;

      customers.forEach(customer => {
        store.put({
          ...customer,
          _lastSync: new Date().toISOString()
        });
        count++;
      });

      transaction.oncomplete = () => {
        console.log(`✅ ${count} clients sauvegardés en local`);
        resolve(count);
      };

      transaction.onerror = (event) => reject(event.target.error);
    });
  }, 0);
};

export const getCustomers = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.CUSTOMERS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }, []);
};

export const searchCustomersByPhone = async (phone) => {
  const customers = await getCustomers();
  if (!customers) return [];
  return customers.filter(c => c.phone?.includes(phone));
};

// ============================================
// 💰 OPÉRATIONS SUR LES VENTES
// ============================================

export const createOfflineSale = async (saleData) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PENDING_SALES, 'readwrite');
    
    const offlineSale = {
      ...saleData,
      localId: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      status: 'pending',
      syncAttempts: 0,
      lastSyncAttempt: null
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(offlineSale);
      
      request.onsuccess = () => {
        console.log('✅ Vente offline créée:', offlineSale.localId);
        resolve(offlineSale);
      };
      
      request.onerror = (event) => {
        console.error('❌ Erreur création vente offline:', event.target.error);
        reject(event.target.error);
      };
    });
  }, null);
};

export const getPendingSales = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.PENDING_SALES);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });
  }, []);
};

export const updatePendingSaleStatus = async (localId, status, serverId = null) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PENDING_SALES, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.localId === localId) {
            const updatedSale = {
              ...cursor.value,
              status,
              serverId,
              syncAttempts: cursor.value.syncAttempts + 1,
              lastSyncAttempt: new Date().toISOString()
            };
            cursor.update(updatedSale);
            resolve(updatedSale);
          } else {
            cursor.continue();
          }
        } else {
          resolve(null);
        }
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  }, null);
};

export const removeSyncedSale = async (localId) => {
  return safeOperation(async () => {
    const store = getStore(STORES.PENDING_SALES, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.localId === localId) {
            cursor.delete();
            console.log('✅ Vente synchronisée supprimée du cache:', localId);
            resolve(true);
          } else {
            cursor.continue();
          }
        } else {
          resolve(false);
        }
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  }, false);
};

export const saveSalesHistory = async (sales) => {
  return safeOperation(async () => {
    const store = getStore(STORES.SALES_HISTORY, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      const transaction = store.transaction;

      sales.forEach(sale => {
        store.put({
          ...sale,
          _lastSync: new Date().toISOString()
        });
        count++;
      });

      transaction.oncomplete = () => {
        console.log(`✅ ${count} ventes historiques sauvegardées`);
        resolve(count);
      };

      transaction.onerror = (event) => reject(event.target.error);
    });
  }, 0);
};

export const getSalesHistory = async (limit = 50) => {
  return safeOperation(async () => {
    const store = getStore(STORES.SALES_HISTORY);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const sales = request.result
          .sort((a, b) => new Date(b.saleDate) - new Date(a.saleDate))
          .slice(0, limit);
        resolve(sales);
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  }, []);
};

// ============================================
// 🔄 FILE D'ATTENTE DE SYNCHRONISATION
// ============================================

export const addToSyncQueue = async (item) => {
  return safeOperation(async () => {
    const store = getStore(STORES.SYNC_QUEUE, 'readwrite');
    
    const queueItem = {
      ...item,
      createdAt: new Date().toISOString(),
      attempts: 0,
      lastAttempt: null
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(queueItem);
      request.onsuccess = () => {
        console.log('📥 Ajouté à la file de sync:', item.type);
        resolve(queueItem);
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }, null);
};

export const getSyncQueue = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const sorted = request.result.sort((a, b) => {
          if (a.priority !== b.priority) return a.priority - b.priority;
          return new Date(a.createdAt) - new Date(b.createdAt);
        });
        resolve(sorted);
      };
      
      request.onerror = (event) => reject(event.target.error);
    });
  }, []);
};

export const removeFromSyncQueue = async (id) => {
  return safeOperation(async () => {
    const store = getStore(STORES.SYNC_QUEUE, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(true);
      request.onerror = (event) => reject(event.target.error);
    });
  }, false);
};

export const clearSyncQueue = async () => {
  return safeOperation(async () => {
    const store = getStore(STORES.SYNC_QUEUE, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log('✅ File de synchronisation vidée');
        resolve(true);
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }, false);
};

// ============================================
// 📊 MÉTADONNÉES
// ============================================

export const setMeta = async (key, value) => {
  return safeOperation(async () => {
    const store = getStore(STORES.META, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.put({ key, value, updatedAt: new Date().toISOString() });
      request.onsuccess = () => resolve(true);
      request.onerror = (event) => reject(event.target.error);
    });
  }, false);
};

export const getMeta = async (key) => {
  return safeOperation(async () => {
    const store = getStore(STORES.META);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = (event) => reject(event.target.error);
    });
  }, null);
};

export const getLastSyncTime = async () => {
  return getMeta('lastSyncTime');
};

export const setLastSyncTime = async () => {
  return setMeta('lastSyncTime', new Date().toISOString());
};

// ============================================
// 🧹 NETTOYAGE
// ============================================

export const clearAllData = async () => {
  if (!db) return;
  
  const storeNames = Object.values(STORES);
  
  for (const storeName of storeNames) {
    try {
      const store = getStore(storeName, 'readwrite');
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = (event) => reject(event.target.error);
      });
    } catch (e) {
      console.warn(`Erreur nettoyage store ${storeName}:`, e);
    }
  }
  
  console.log('✅ Toutes les données locales effacées');
};

export const getStorageStats = async () => {
  if (!db) {
    return {
      PRODUCTS: 0,
      CATEGORIES: 0,
      CUSTOMERS: 0,
      PENDING_SALES: 0,
      SALES_HISTORY: 0,
      SYNC_QUEUE: 0,
      META: 0
    };
  }
  
  const stats = {};
  
  for (const [name, storeName] of Object.entries(STORES)) {
    try {
      const store = getStore(storeName);
      const count = await new Promise((resolve) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });
      stats[name] = count;
    } catch (e) {
      stats[name] = 0;
    }
  }
  
  return stats;
};

// Export des constantes
export { STORES };

export default {
  initDatabase,
  isReady,
  // Produits
  saveProducts,
  getProducts,
  getProductByBarcode,
  searchProductsByName,
  updateLocalStock,
  // Catégories
  saveCategories,
  getCategories,
  // Clients
  saveCustomers,
  getCustomers,
  searchCustomersByPhone,
  // Ventes
  createOfflineSale,
  getPendingSales,
  updatePendingSaleStatus,
  removeSyncedSale,
  saveSalesHistory,
  getSalesHistory,
  // Sync queue
  addToSyncQueue,
  getSyncQueue,
  removeFromSyncQueue,
  clearSyncQueue,
  // Meta
  setMeta,
  getMeta,
  getLastSyncTime,
  setLastSyncTime,
  // Utils
  clearAllData,
  getStorageStats,
  STORES
};